// @flow
import { combineReducers } from 'redux';

import { reducer as acl } from '@gandi/acl';

import { reducer as authenticationReducer } from '@gandi/authentication';

import {
  infosReducer as gandiReduxBillingInfosReducer,
  prepaidReducer as gandiReduxBillingPrepaidReducer,
  currenciesReducer as gandiReduxBillingCurrenciesReducer,
  pricingReducer as gandiReduxBillingPricingReducer,
  ordersReducer as gandiReduxBillingOrdersReducer,
  meansReducer as gandiReduxBillingMeanReducer,
  moneyReducer as gandiReduxBillingMoneyReducer,
} from '@gandi/billing';
import { reducer as cartReducer } from '@gandi/cart';
import { reducer as contractsReducer } from '@gandi/contracts';
import { reducer as domainContactsReducer } from '@gandi/domain-configuration';
import { reducer as notificationsReducer } from '@gandi/notifications';

import { organizationsReducer } from '@gandi/redux-organization';
import { reducer as gandiReduxPlaces } from '@gandi/redux-places';
import { reducer as gandiReduxResourceContactBookReducer } from '@gandi/redux-resource-contactbook';

import gandiReduxResourceCoupon from '@gandi/redux-resource-coupon';
import gandiReduxResourcePrice from '@gandi/redux-resource-price';
import { reducer as gandiReduxResourceSimplehosting } from '@gandi/redux-resource-simplehosting';
import { reducer as sharingReducer } from '@gandi/redux-sharing';
import { reducer as gandiReduxResourceReducer } from '@gandi/resource/redux';
import { reducer as template2Reducer } from '@gandi/template2';

import { csrfReducer, reducer as gandiFormReducer } from 'gandi.react-form';

import customersCreateReducer from './Cart/Customer/modules/customers';
import { reducer as domainContactsWizardReducer } from './Cart/DomainContacts/Wizard/modules/reducer';
import setupReducer from './Cart/Setup/modules/reducers';
import corporateReducer from './Cart/modules/corporate';

import phasesModalReducer from './DomainCreate/components/PhasesDialog/module';
import affiliateReducer from './DomainCreate/modules/affiliate';
import domainBundleReducer from './DomainCreate/modules/reducers/bundle';
import domainDiscountReducer from './DomainCreate/modules/reducers/discount';
import domainFiltersReducer from './DomainCreate/modules/reducers/filters';
import domainIDNReducer from './DomainCreate/modules/reducers/idn';
import domainPaginationReducer from './DomainCreate/modules/reducers/pagination';
import domainResultsReducer from './DomainCreate/modules/reducers/results';
import domainSearchReducer from './DomainCreate/modules/reducers/search';
import domainSourceReducer from './DomainCreate/modules/reducers/source';
import domainTLDsReducer from './DomainCreate/modules/reducers/tlds';
import renewableProductsReducer from './DomainRenew/componentsNDS/modules/reducers';
import domainRenewReducer from './DomainRenew/modules/reducers';
import domainTransferReducer from './DomainTransfer/modules/reducer';

import { reducer as simplehostingDomainReducer } from './SimpleHosting/modules/domains';
import simplehostingTypesReducer from './SimpleHosting/modules/types';

import tmchReducer from './Tmch/modules/reducer';

import { certificateReducer } from './certificate/modules/certificate';

import appReducer from './modules/app/reducer';
import { reducer as cloudAccountsReducer } from './modules/cloud/resources/accounts';
import { reducer as cloudImagesReducer } from './modules/cloud/resources/images';
import { reducer as cloudRegionsReducer } from './modules/cloud/resources/regions';
import { reducer as cloudServersReducer } from './modules/cloud/resources/servers';
import { reducer as cloudTemplatesReducer } from './modules/cloud/resources/templates';
import { reducer as mailboxOffersReducer } from './modules/mailbox/offers';
import { reducer as mailboxStandaloneProductsReducer } from './modules/mailbox-standalone/resources/products';
import { reducer as simplehostingPricesReducer } from './modules/simplehosting/prices';
import { reducer as tldPricesReducer } from './modules/tldpublic/tlds';

const reducer = {
  // app / layout
  app: appReducer,
  notifications: notificationsReducer,
  csrf: csrfReducer,

  // authz & orga
  authentication: authenticationReducer,
  sharing: sharingReducer,

  // libs
  acl,
  cart: cartReducer,
  contracts: contractsReducer,
  gandiReduxPlaces,

  // domain
  domainSuggest: (combineReducers({
    search: domainSearchReducer,
    pagination: domainPaginationReducer,
    results: domainResultsReducer,
    filters: domainFiltersReducer,
    tlds: domainTLDsReducer,
    source: domainSourceReducer,
    idn: domainIDNReducer,
    bundles: domainBundleReducer,
    discount: domainDiscountReducer,
  }): any),
  phasesModal: phasesModalReducer,
  domainRenew: domainRenewReducer,
  domainTransfer: domainTransferReducer,

  // mailboxes
  mailboxOffers: mailboxOffersReducer,
  // certificates
  certificate: certificateReducer,

  // hosting
  simplehosting: (combineReducers({
    domains: simplehostingDomainReducer,
    prices: simplehostingPricesReducer,
    types: simplehostingTypesReducer,
  }): any),
  cloud: (combineReducers({
    accounts: cloudAccountsReducer,
    images: cloudImagesReducer,
    regions: cloudRegionsReducer,
    servers: cloudServersReducer,
    templates: cloudTemplatesReducer,
  }): any),

  // shared
  renewableProducts: renewableProductsReducer,

  // products configuration
  configure: (combineReducers({
    corporate: corporateReducer,
    customers: customersCreateReducer,
    domainContactsWizard: domainContactsWizardReducer,
    // templates: templatesReducer,
    setup: setupReducer,
  }): any),
  domainContacts: domainContactsReducer,
  // TMCH
  tmch: tmchReducer,
  // TLD public
  tldpublic: tldPricesReducer,
  // tools
  gandiForm: gandiFormReducer,
  gandiReduxResource: gandiReduxResourceReducer,
  gandiReduxResourceCoupon,
  gandiReduxResourcePrice,
  gandiReduxBilling: (combineReducers({
    infos: gandiReduxBillingInfosReducer,
    prepaid: gandiReduxBillingPrepaidReducer,
    currencies: gandiReduxBillingCurrenciesReducer,
    pricing: gandiReduxBillingPricingReducer,
    orders: gandiReduxBillingOrdersReducer,
    means: gandiReduxBillingMeanReducer,
    money: gandiReduxBillingMoneyReducer,
  }): any),
  gandiReduxResourceSimplehosting,
  template2: template2Reducer,
  gandiReduxOrganization: (combineReducers({
    organizations: organizationsReducer,
  }): any),
  gandiReduxResourceContactBook: gandiReduxResourceContactBookReducer,
  affiliate: affiliateReducer,
  'mailbox-standalone': (combineReducers({
    products: mailboxStandaloneProductsReducer,
  }): any),
};

export default reducer;
